"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Badge, Input, Row, Col } from "reactstrap"
import { FaEdit, FaPlus, FaSearch, FaBan } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { GateAssignmentService } from "../../../Service/TicketingService"
import type { GateAssignment, PaginatedResponse } from "./types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  onAdd: () => void
  onEdit: (assignment: GateAssignment) => void
  refreshTrigger: boolean
  onRefresh: () => void
}

const GateAssignmentList: React.FC<Props> = ({ onAdd, onEdit, refreshTrigger, onRefresh }) => {
  const [assignments, setAssignments] = useState<GateAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const fetchAssignments = async (page = 1, searchTerm = "", status = "") => {
    try {
      setLoading(true)
      const response: PaginatedResponse<GateAssignment> = await GateAssignmentService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
        status,
      )
      setAssignments(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch gate assignments:", error)
      toast.error("Failed to load gate assignments")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssignments(1, search, statusFilter)
  }, [refreshTrigger])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchAssignments(1, search, statusFilter)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchAssignments(1, search, status)
  }

  const handleRevoke = async (assignment: GateAssignment) => {
    const result = await Swal.fire({
      title: "Revoke Assignment?",
      text: `Revoke gate ${assignment.gate} from ${assignment.user_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, revoke it!",
    })

    if (result.isConfirmed) {
      try {
        await GateAssignmentService.revoke(assignment.id)
        await Swal.fire("Revoked!", "Gate assignment has been revoked.", "success")
        onRefresh()
      } catch (error) {
        console.error("Failed to revoke assignment:", error)
        await Swal.fire("Error", "Failed to revoke assignment.", "error")
      }
    }
  }

  const getStatusBadge = (is_active: boolean) => {
    return (
      <Badge color={is_active ? "success" : "danger"} pill>
        {is_active ? "Active" : "Inactive"}
      </Badge>
    )
  }

  const getGateBadge = (gate: string) => {
    const gateColors: { [key: string]: string } = {
      entry_gate: "primary",
      exit_gate: "secondary",
      main_gate: "success",
      service_gate: "warning",
      vip_gate: "info",
    }
    return (
      <Badge color={gateColors[gate] || "light"} pill>
        {gate.replace("_", " ").toUpperCase()}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    fetchAssignments(page, search, statusFilter)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchAssignments(page, search, statusFilter)
  }

  const columns: TableColumn<GateAssignment>[] = [
    {
      name: "#",
      selector: (row, index) => (pagination.currentPage - 1) * pagination.pageSize + (index || 0) + 1,
      width: "80px",
    },
    {
      name: "User",
      selector: (row) => row.user_name,
      sortable: true,
      cell: (row) => <strong>{row.user_name}</strong>,
    },
    {
      name: "Gate",
      selector: (row) => row.gate,
      sortable: true,
      cell: (row) => getGateBadge(row.gate),
    },
    {
      name: "Status",
      selector: (row) => row.is_active,
      sortable: true,
      cell: (row) => getStatusBadge(row.is_active),
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => (
        <div>
          <div>{new Date(row.created_at).toLocaleDateString()}</div>
          <small className="text-muted">{new Date(row.created_at).toLocaleTimeString()}</small>
        </div>
      ),
    },
    {
      name: "Created By",
      selector: (row) => row.created_by_name || "System",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          <Btn color="info" size="sm" onClick={() => onEdit(row)} title="Edit">
            <FaEdit />
          </Btn>
          {row.is_active && (
            <Btn color="warning" size="sm" onClick={() => handleRevoke(row)} title="Revoke">
              <FaBan />
            </Btn>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ]

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">Gate Assignments ({pagination.totalCount})</h5>
          </Col>
          <Col md="6" className="text-end">
            <Btn color="primary" size="sm" onClick={onAdd}>
              <FaPlus className="me-2" />
              New Assignment
            </Btn>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Search and Filters */}
        <form onSubmit={handleSearch} className="mb-3">
          <Row>
            <Col md="4">
              <div className="input-group">
                <Input
                  type="text"
                  placeholder="Search assignments..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary btn-sm" type="submit">
                  <FaSearch />
                </button>
              </div>
            </Col>
            <Col md="3">
              <Input type="select" value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Input>
            </Col>
          </Row>
        </form>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={assignments}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent="No gate assignments found"
          striped
          highlightOnHover
          responsive
        />
      </CardBody>
    </Card>
  )
}

export default GateAssignmentList
